import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import AirportIcon from "assets/images/Group.svg"
import FilterFlight from "assets/images/FilterFlight.svg"
import VerticalFlightIcon from "assets/images/VerticalFlightIcon.svg"
import { FlightDetails, ConnectionFlight } from "./types";
import SearchResults from "./SearchResults";
import { ArrowRightLeft, Search, ChevronDown, Check, TowerControl, History, X, Plane } from "lucide-react";
import MicIcon from "assets/images/mic-icon.png";
import { useClickOutside } from "../../hooks/useClickOutside";
import {
  fetchFlights,
  fetchFlightSuggestions,
  FlightSuggestion,
  FlightSuggestionDirectiveFilters,
  getLastRoute,
  getRecentSearches,
  saveRecentSearch,
  RecentSearchItem,
  SaveRecentSearchPayload,
} from "apis/loadsServices";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { useToastStore } from "store/toastStore";
import "react-datepicker/dist/react-datepicker.css";
import SwapIcon from "../../assets/images/swap.png";
import VoiceListeningPanel, { VoicePanelPhase } from "../../voice/ui/VoiceListeningPanel";
import VoiceTranscriptConfirm from "../../voice/ui/VoiceTranscriptConfirm";
import VoiceAmbiguityChooser from "../../voice/ui/VoiceAmbiguityChooser";
import { useWebVoiceSearch } from "../../voice/web/useWebVoiceSearch";
import { VoiceCriteria } from "../../voice/core/types";
import AirportField from "./AirportField";
import DateRangeCalendar from "./DateRangeCalendar";
import NearbyAirportsToggle from "./NearbyAirportsToggle";
import FilterSortBar, { DurationSortOrder } from "./FilterSortBar";
import RoutePager from "./RoutePager";
import RecentSearchesDropdown from "./RecentSearchesDropdown";
import AiSuggestiveFilters from "./AiSuggestiveFilters";
import { useAirportCodes } from "./useAirportCodes";
import { useFlightNumbers } from "./useFlightNumbers";
import {
  mapStopsFilterToParams,
  mapStopsFilterToDisplayIntent,
  withDirectFlightCompositeIds,
  withConnectionCompositeIds,
  calculateEffectiveTotalFlights,
  extractMainName,
  normalizeFlightNumber,
} from "./routeSearchHelpers";
import {
  formatDateLocal,
  saveLastSearchToStorage,
  loadLastSearchFromStorage,
  saveRecentLocation,
  loadRecentLocations,
  RecentLocation,
  LastSearchData,
} from "./searchStorage";

// Custom styles for DatePicker
const datePickerStyles = `
  .react-datepicker__day--highlighted {
    background-color: #FFD700 !important;
    color: #000 !important;
  }
  .react-datepicker__day--highlighted:hover {
    background-color: #FFC700 !important;
  }
  /* react-datepicker tracks the last-clicked day as "keyboard-selected"
     independently of our own highlightDates. Clicking an already-selected
     day to remove it clears our highlight but the library still paints it
     as keyboard-selected until another day is clicked. Suppress that style
     unless the day is also one we actually still consider selected. */
  .react-datepicker__day--keyboard-selected:not(.react-datepicker__day--highlighted) {
    background-color: transparent !important;
    color: inherit !important;
  }
  #datepicker-portal .react-datepicker-popper {
    z-index: 99999 !important;
  }
`;

interface LastRouteResponse {
  lastSearchFlightNumber: string | null;
  lastSearchRoute: {
    source: string;
    destination: string;
    date: string;
  };
}

interface SearchByRouteProps {
  onShowSearchResults?: (show: boolean) => void;
  onPostRequestSuccess?: (
    optimisticItems?: any[],
    options?: { keepResults?: boolean },
  ) => void;
  tabButtons?: React.ReactNode;
  onBack?: () => void;
  readOnly?: boolean;
  breadcrumbLabel?: string;
  /** Retired — this form handles a heard flight number itself now. */
  // onVoiceFlightNumberDetected?: (flightNumber: string) => void;
}

const SearchByRoute: React.FC<SearchByRouteProps> = ({
  onShowSearchResults,
  onPostRequestSuccess,
  tabButtons,
  onBack,
  readOnly = false,
  breadcrumbLabel = "Loads",
}) => {
  const [selectedDeparture, setSelectedDeparture] = useState<"yes" | "no">(
    "no",
  );
  const [selectedArrival, setSelectedArrival] = useState<"yes" | "no">("no");
  const [stopsFilter, setStopsFilter] = useState<
    "none" | "one-or-fewer" | "one" | "two" | "three" | null
  >("none");
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<DurationSortOrder | string | null>(null);
  const [suggestions, setSuggestions] = useState<FlightSuggestion[]>([]);
  const [appliedSuggestionId, setAppliedSuggestionId] = useState<string | null>(null);
  const [appliedSuggestion, setAppliedSuggestion] = useState<FlightSuggestion | null>(null);
  const [facets, setFacets] = useState<any>(null);
  const [appliedDirectiveFilters, setAppliedDirectiveFilters] = useState<FlightSuggestionDirectiveFilters | null>(null);
  const [priorManualState, setPriorManualState] = useState<{
    stopsFilter: "none" | "one-or-fewer" | "one" | "two" | "three" | null;
    sortBy: string | null;
    sortOrder: DurationSortOrder | string | null;
    directiveFilters: FlightSuggestionDirectiveFilters | null;
  } | null>(null);
  const [fromLocation, setFromLocation] = useState<string>("");
  const [toLocation, setToLocation] = useState<string>("");
  const [flightNumber, setFlightNumber] = useState<string>("");
  const { isLoadingFlightNumbers, filterFlightNumbers, isValidFlightNumber } =
    useFlightNumbers();
  const [showFlightSuggestions, setShowFlightSuggestions] = useState(false);
  const flightFieldRef = useRef<HTMLDivElement | null>(null);

  const flightNumberOptions = useMemo(
    () =>
      filterFlightNumbers(flightNumber).map((value) => ({
        value,
        label: value,
      })),
    [filterFlightNumbers, flightNumber],
  );

  useClickOutside(flightFieldRef, () => setShowFlightSuggestions(false));
  const [date, setDate] = useState<string>(
    formatDateLocal(new Date(Date.now() + 86400000)),
  ); // Default to Tomorrow's date
  const [showResults, setShowResults] = useState(false);
  const [errors, setErrors] = useState<{
    from?: string;
    to?: string;
    date?: string;
    flightNumber?: string;
  }>({});
  const [flights, setFlights] = useState<FlightDetails[]>([]);
  const [connectionFlights, setConnectionFlights] = useState<
    ConnectionFlight[]
  >([]);
  // Set when the flight search request itself fails (e.g. a 500), as
  // distinct from a successful response with zero results — the two need
  // different UI (an error state vs. the normal "no flights found" state).
  const [searchError, setSearchError] = useState<string | null>(null);
  // The API's own message for a valid zero-result response (e.g. a flight
  // number that doesn't operate on the selected route).
  const [noResultsMessage, setNoResultsMessage] = useState<string | null>(
    null,
  );
  const [isLoadingFlights, setIsLoadingFlights] = useState(false);
  const [isLoadingLastSearch, setIsLoadingLastSearch] = useState(false);
  const { showLoader, hideLoader } = useGlobalLoader();
  const [hasSearched, setHasSearched] = useState(false);
  const [PageNumber, setPageNumber] = useState(1);
  const [totalFlights, setTotalFlights] = useState(0);
  // API response's top-level `count` field, used only for the
  // "Select all N Results" label.
  const [apiResultCount, setApiResultCount] = useState(0);
  // Raw direct/connection totals from the API, used to distinguish
  // "no direct flights (but connections exist)" from "no flights at all".
  const [directTotal, setDirectTotal] = useState<number | undefined>(undefined);
  const [connectionTotal, setConnectionTotal] = useState<number | undefined>(undefined);
  // Bumped to force the Filter dropdown open when "Widen search" is clicked.
  const [openFilterSignal, setOpenFilterSignal] = useState<number | undefined>(
    undefined,
  );
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [datesInRange, setDatesInRange] = useState<string[]>([]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Flight Status (readOnly Loads) may only look up Yesterday/Today/Tomorrow —
  // no broader ranges and no future/past dates beyond that window.
  const flightStatusMinDate = useMemo(() => {
    if (!readOnly) return undefined;
    const d = new Date();
    d.setDate(d.getDate() - 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [readOnly]);
  const flightStatusMaxDate = useMemo(() => {
    if (!readOnly) return undefined;
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [readOnly]);

  // Recent locations state
  const [recentFromLocations, setRecentFromLocations] = useState<
    RecentLocation[]
  >([]);
  const [recentToLocations, setRecentToLocations] = useState<RecentLocation[]>(
    [],
  );
  const [isFromCardOpen, setIsFromCardOpen] = useState<boolean>(false);
  const [isToCardOpen, setIsToCardOpen] = useState<boolean>(false);
  const [fromSearchInput, setFromSearchInput] = useState<string>("");
  const [toSearchInput, setToSearchInput] = useState<string>("");

  // Recent searches (route) state
  const [recentRouteSearches, setRecentRouteSearches] = useState<
    RecentSearchItem[]
  >([]);
  const [isLoadingRecentSearches, setIsLoadingRecentSearches] =
    useState(false);
  const [isRecentSearchesOpen, setIsRecentSearchesOpen] = useState(false);
  const [isNearbyAirportsOpen, setIsNearbyAirportsOpen] = useState(false);
  const [isStopsOpen, setIsStopsOpen] = useState(false);

  const nearbyAirportsRef = useRef<HTMLDivElement>(null);
  const stopsRef = useRef<HTMLDivElement>(null);

  useClickOutside(nearbyAirportsRef, () => setIsNearbyAirportsOpen(false));
  useClickOutside(stopsRef, () => setIsStopsOpen(false));

  const flightsPerPage = 10;

  // A Week/Month calendar selection sends a date RANGE (not a single day) —
  // the API is asked for more results per page (50) for those searches so a
  // wide date range doesn't take dozens of round trips. `totalPages` and the
  // "select all" prefetch below must use this SAME page size the API request
  // actually uses, or the UI ends up offering more pages than really exist
  // (computed off 9-per-page) while asking the API for 50-per-page — pages
  // past the real end come back empty even though the UI still shows more.
  const isMonthOrWeekSearch = useMemo(
    () =>
      datesInRange.some((d) => {
        if (!d.includes(" - ")) return false;
        const [startStr, endStr] = d.split(" - ");
        const dStart = new Date(`${startStr}T00:00:00`);
        const dEnd = new Date(`${endStr}T00:00:00`);
        if (isNaN(dStart.getTime()) || isNaN(dEnd.getTime())) return false;
        const diffDays = Math.round(
          (dEnd.getTime() - dStart.getTime()) / (1000 * 3600 * 24),
        );
        return diffDays > 14;
      }),
    [datesInRange],
  );
  const effectivePageSize = isMonthOrWeekSearch ? 50 : flightsPerPage;
  const totalPages = Math.ceil(totalFlights / effectivePageSize);

  // Create options for react-select with combined code and name - memoized to prevent re-renders
  // Airport autocomplete data (From/To fields).
  const {
    departureOptions,
    arrivalOptions,
    isLoadingDepartures,
    isLoadingArrivals,
    loadDepartureCodes,
    loadArrivalCodes,
    resetArrivalSearch,
  } = useAirportCodes(fromLocation, toLocation);

  // Load recent locations from localStorage on component mount
  useEffect(() => {
    const fromLocs = loadRecentLocations("from");
    const toLocs = loadRecentLocations("to");
    setRecentFromLocations(fromLocs);
    setRecentToLocations(toLocs);
  }, []);

  // Fetch recent searches (server-backed) on mount.
  const fetchRecentRouteSearches = async () => {
    setIsLoadingRecentSearches(true);
    try {
      const items = await getRecentSearches(10);
      setRecentRouteSearches(items);
    } finally {
      setIsLoadingRecentSearches(false);
    }
  };

  useEffect(() => {
    fetchRecentRouteSearches();
  }, []);

  // Selecting a recent search: populate From/To fields and/or Flight No field (and open the
  // calendar if no date has been chosen yet), mirroring manual selection.
  const handleRecentSearchSelect = (item: RecentSearchItem) => {
    if (item.sourceCode && item.destinationCode) {
      setFromLocation(item.sourceCode);
      setToLocation(item.destinationCode);

      if (item.sourceName) {
        saveRecentLocation("from", item.sourceCode, item.sourceName);
        setRecentFromLocations(loadRecentLocations("from"));
      }
      if (item.destinationName) {
        saveRecentLocation("to", item.destinationCode, item.destinationName);
        setRecentToLocations(loadRecentLocations("to"));
      }
    } else {
      setFromLocation("");
      setToLocation("");
    }

    if (item.flightNumber) {
      setFlightNumber(item.flightNumber);
    } else {
      setFlightNumber("");
    }

    setErrors({});

    if (selectedDates.length === 0) {
      setTimeout(() => setIsCalendarOpen(true), 100);
    }

    fetchRecentRouteSearches();
  };

  // Resolve the airport name for a code from its options list or recent history.
  const resolveLocationName = (
    code: string,
    options: { value: string; label: string }[],
    recents: RecentLocation[],
  ): string => {
    if (!code) return "";
    const option = options.find((o) => o.value === code);
    if (option) {
      const dashIdx = option.label.indexOf(" - ");
      return dashIdx >= 0 ? option.label.slice(dashIdx + 3) : option.label;
    }
    return recents.find((r) => r.code === code)?.name || "";
  };

  // Display text for a field's selected input (main city/location name, e.g. "Dubai", "Cochin").
  const resolveFieldDisplay = (
    code: string,
    options: { value: string; label: string }[],
    recents: RecentLocation[],
  ): string => {
    if (!code) return "";
    const option = options.find((o) => o.value === code);
    if (option) return extractMainName(option.label);
    const recent = recents.find((l) => l.code === code);
    if (recent) return extractMainName(recent.name);
    return code;
  };

  // Selecting a From airport: persist it, clear conflicts, advance to "To".
  const handleSelectFrom = (code: string, name: string) => {
    setFromLocation(code);
    setErrors((prev) => ({ ...prev, from: undefined }));
    saveRecentLocation("from", code, name);
    setRecentFromLocations(loadRecentLocations("from"));
    if (code === toLocation) {
      setToLocation("");
      setErrors((prev) => ({
        ...prev,
        to: "Departure and arrival locations cannot be the same",
      }));
    }
    setIsFromCardOpen(false);
    setFromSearchInput("");
    setTimeout(() => setIsToCardOpen(true), 100);
  };

  // Selecting a To airport: persist it, clear conflicts, open the calendar.
  const handleSelectTo = (code: string, name: string) => {
    setToLocation(code);
    setErrors((prev) => ({ ...prev, to: undefined }));
    saveRecentLocation("to", code, name);
    setRecentToLocations(loadRecentLocations("to"));
    if (code === fromLocation) {
      setFromLocation("");
      setErrors((prev) => ({
        ...prev,
        from: "Departure and arrival locations cannot be the same",
      }));
    }
    setIsToCardOpen(false);
    setToSearchInput("");
    resetArrivalSearch();
    // Only auto-open the date picker if no date has been chosen yet.
    if (selectedDates.length === 0) {
      setTimeout(() => setIsCalendarOpen(true), 100);
    }
  };

  // Swap the Flying From / Going to selections.
  const handleSwapLocations = () => {
    if (!fromLocation && !toLocation) return;

    const newFromCode = toLocation;
    const newToCode = fromLocation;
    const newFromName = resolveLocationName(
      toLocation,
      arrivalOptions,
      recentToLocations,
    );
    const newToName = resolveLocationName(
      fromLocation,
      departureOptions,
      recentFromLocations,
    );

    setFromLocation(newFromCode);
    setToLocation(newToCode);
    setFromSearchInput(toSearchInput);
    setToSearchInput(fromSearchInput);

    // Persist names under the new field type so the swapped codes still render
    // their full "CODE - Name" labels instead of a bare code.
    if (newFromCode && newFromName) {
      saveRecentLocation("from", newFromCode, newFromName);
      setRecentFromLocations(loadRecentLocations("from"));
    }
    if (newToCode && newToName) {
      saveRecentLocation("to", newToCode, newToName);
      setRecentToLocations(loadRecentLocations("to"));
    }

    setErrors((prev) => ({ ...prev, from: undefined, to: undefined }));
  };

  // Clear "To" field if same value is selected in "From"
  useEffect(() => {
    if (fromLocation && fromLocation === toLocation) {
      setToLocation("");
      setErrors((prev) => ({
        ...prev,
        to: "Departure and arrival locations cannot be the same",
      }));
    }
  }, [fromLocation]);

  // Clear "From" field if same value is selected in "To"
  useEffect(() => {
    if (toLocation && toLocation === fromLocation) {
      setFromLocation("");
      setErrors((prev) => ({
        ...prev,
        from: "Departure and arrival locations cannot be the same",
      }));
    }
  }, [toLocation]);

  // Track previous filter value to detect changes
  const prevStopsFilter = useRef<
    "none" | "one-or-fewer" | "one" | "two" | "three" | null
  >("none");
  // Track previous sort values to detect changes
  const prevSortBy = useRef<string | null>(null);
  const prevSortOrder = useRef<DurationSortOrder | string | null>(null);
  // Track previous page number to detect page changes
  const prevPageNumber = useRef<number>(1);
  // Guards against a slower, stale FlightSuggestions response (e.g. from the
  // initial mixed-results load) overwriting a newer one fired by a filter change.
  const suggestionsRequestSeq = useRef(0);

  // Handler for stops filter change - sets loading state immediately
  const handleStopsFilterChange = (
    filter: "none" | "one-or-fewer" | "one" | "two" | "three" | null,
  ) => {
    // Manual filter edit clears active chip selection state, including its
    // directive filters — otherwise a stale AI directive (e.g. includeDirect:
    // false from "fewest stops") keeps overriding the new stops filter.
    setAppliedSuggestionId(null);
    setAppliedSuggestion(null);
    setAppliedDirectiveFilters(null);
    const filterChanged = hasSearched && prevStopsFilter.current !== filter;

    if (filterChanged) {
      setIsLoadingFlights(true);
    }

    setStopsFilter(filter);
  };

  // Handler for sort changes (from manual dropdown) - sets loading state immediately
  const handleSortChange = (
    newSortBy: string | null,
    newSortOrder: DurationSortOrder | string | null,
  ) => {
    // Manual sort edit clears active chip selection state, including its
    // directive filters (see handleStopsFilterChange for why).
    setAppliedSuggestionId(null);
    setAppliedSuggestion(null);
    setAppliedDirectiveFilters(null);
    const sortChanged =
      hasSearched &&
      (prevSortBy.current !== newSortBy || prevSortOrder.current !== newSortOrder);
    if (sortChanged) {
      setIsLoadingFlights(true);
    }
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };

  // AI Suggestive Filter application handler
  const handleSelectSuggestion = (suggestion: FlightSuggestion) => {
    // Base every chip application off the user's manual state (captured
    // before the first chip was ever applied), so switching between chips
    // never compounds a previous chip's directive on top of another.
    let baseState = priorManualState;
    if (!baseState) {
      baseState = {
        stopsFilter,
        sortBy,
        sortOrder,
        directiveFilters: appliedDirectiveFilters,
      };
      setPriorManualState(baseState);
    }

    const directive = suggestion.directive || {};
    const newDirectiveFilters = directive.filters || {};

    let newSortBy = baseState.sortBy;
    if (directive.sortBy !== undefined && directive.sortBy !== null) {
      newSortBy = directive.sortBy;
    }

    let newSortOrder = baseState.sortOrder;
    if (directive.sortDirection !== undefined && directive.sortDirection !== null) {
      newSortOrder = directive.sortDirection;
    }

    let newStopsFilter = baseState.stopsFilter;
    if (typeof newDirectiveFilters.stops === "number") {
      if (newDirectiveFilters.stops === 0) newStopsFilter = "none";
      else if (newDirectiveFilters.stops === 1) newStopsFilter = "one";
      else if (newDirectiveFilters.stops === 2) newStopsFilter = "two";
      else if (newDirectiveFilters.stops === 3) newStopsFilter = "three";
    } else if (
      newDirectiveFilters.includeDirect === true &&
      newDirectiveFilters.includeConnections === false
    ) {
      newStopsFilter = "none";
    } else if (
      newDirectiveFilters.includeDirect === false &&
      newDirectiveFilters.includeConnections === true
    ) {
      newStopsFilter = "one-or-fewer";
    } else if (
      newDirectiveFilters.includeDirect === true &&
      newDirectiveFilters.includeConnections === true
    ) {
      newStopsFilter = "none";
    }

    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setStopsFilter(newStopsFilter);
    setAppliedDirectiveFilters(newDirectiveFilters);
    setAppliedSuggestionId(suggestion.id);
    setAppliedSuggestion(suggestion);
    setPageNumber(1);
    prevPageNumber.current = 1;

    setIsLoadingFlights(true);
    getFlights({
      pageNumber: 1,
      stopsFilter: newStopsFilter,
      sortBy: newSortBy,
      sortOrder: newSortOrder,
      directiveFilters: newDirectiveFilters,
      appliedSuggestionId: suggestion.id,
    });
  };

  // AI Suggestive Filter dismiss handler — clears only the applied AI
  // suggestion, leaving the current stops filter/sort/directive filters as-is.
  const handleDismissSuggestion = () => {
    // Restore whatever sort/filters the user had set manually before the
    // first AI chip was applied, rather than the AI-modified current state.
    const restoredStopsFilter = priorManualState?.stopsFilter ?? stopsFilter;
    const restoredSortBy = priorManualState?.sortBy ?? sortBy;
    const restoredSortOrder = priorManualState?.sortOrder ?? sortOrder;
    const restoredDirectiveFilters = priorManualState?.directiveFilters ?? appliedDirectiveFilters;

    setAppliedSuggestionId(null);
    setAppliedSuggestion(null);
    setStopsFilter(restoredStopsFilter);
    setSortBy(restoredSortBy);
    setSortOrder(restoredSortOrder);
    setAppliedDirectiveFilters(restoredDirectiveFilters);
    setPriorManualState(null);
    setPageNumber(1);
    prevPageNumber.current = 1;

    setIsLoadingFlights(true);
    getFlights({
      pageNumber: 1,
      stopsFilter: restoredStopsFilter,
      sortBy: restoredSortBy,
      sortOrder: restoredSortOrder,
      directiveFilters: restoredDirectiveFilters,
      appliedSuggestionId: null,
    });
  };

  // Fetch flights when the stops filter or sort parameters change (reset to page 1 first)
  useEffect(() => {
    // Only fetch if filter/sort actually changed and user has searched before
    if (
      hasSearched &&
      (prevStopsFilter.current !== stopsFilter ||
        prevSortBy.current !== sortBy ||
        prevSortOrder.current !== sortOrder)
    ) {
      // Reset to page 1 if needed - update prevPageNumber to prevent page change effect
      const needsPageReset = PageNumber !== 1;
      if (needsPageReset) {
        prevPageNumber.current = PageNumber; // Mark that we're resetting, not changing
        setPageNumber(1);
      }

      // Use requestAnimationFrame to ensure React has rendered the skeleton before starting API call
      const fetchWithFilter = async () => {
        try {
          // Wait for React to render the skeleton
          await new Promise((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                resolve(undefined);
              });
            });
          });

          // Additional small delay to ensure skeleton is visible
          await new Promise((resolve) => setTimeout(resolve, 100));

          await getFlights();

          // Scroll to search results
          setTimeout(() => {
            const searchResultsElement =
              document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights for filter:", error);
        }
      };

      fetchWithFilter();
    }
    // Update previous filter/sort values after checking
    if (hasSearched) {
      prevStopsFilter.current = stopsFilter;
      prevSortBy.current = sortBy;
      prevSortOrder.current = sortOrder;
    }
  }, [stopsFilter, sortBy, sortOrder, hasSearched, PageNumber]);

  // Track Nearby Airports selection at the moment the dropdown opens, so we
  // only refresh when it actually changed while open.
  const prevSelectedDeparture = useRef(selectedDeparture);
  const prevSelectedArrival = useRef(selectedArrival);
  const prevNearbyAirportsOpen = useRef(isNearbyAirportsOpen);

  // Refresh results on leaving (closing) the Nearby Airports dropdown, only
  // if Departure/Arrival selection changed while it was open.
  useEffect(() => {
    const wasOpen = prevNearbyAirportsOpen.current;
    prevNearbyAirportsOpen.current = isNearbyAirportsOpen;

    if (!wasOpen && isNearbyAirportsOpen) {
      // Dropdown just opened - snapshot selection to detect changes on close
      prevSelectedDeparture.current = selectedDeparture;
      prevSelectedArrival.current = selectedArrival;
      return;
    }

    if (wasOpen && !isNearbyAirportsOpen) {
      const changed =
        prevSelectedDeparture.current !== selectedDeparture ||
        prevSelectedArrival.current !== selectedArrival;
      prevSelectedDeparture.current = selectedDeparture;
      prevSelectedArrival.current = selectedArrival;

      if (hasSearched && changed) {
        setPageNumber(1);
        prevPageNumber.current = 1;
        getFlights({ pageNumber: 1 });
      }
    }
  }, [isNearbyAirportsOpen, selectedDeparture, selectedArrival, hasSearched]);

  // Handler for page number change - sets loading state immediately
  const handlePageChange = (page: number) => {
    if (hasSearched && PageNumber !== page) {
      // Set loading state immediately - this happens synchronously
      setIsLoadingFlights(true);
      // Then update the page number which will trigger the useEffect
      setPageNumber(page);
    }
  };

  // Fetch flights when page number changes (but not on initial load or filter change)
  useEffect(() => {
    // Only fetch if page actually changed (not initial load or filter reset)
    if (
      hasSearched &&
      prevPageNumber.current !== PageNumber &&
      prevPageNumber.current !== null
    ) {
      const fetchFlightsForPage = async () => {
        try {
          // Wait for React to render the skeleton
          await new Promise((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                resolve(undefined);
              });
            });
          });

          // Additional small delay to ensure skeleton is visible
          await new Promise((resolve) => setTimeout(resolve, 100));

          await getFlights();

          // Scroll to search results after page change
          setTimeout(() => {
            const searchResultsElement =
              document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights for page:", error);
        }
      };
      fetchFlightsForPage();
    }
    // Update previous page number after checking
    if (hasSearched) {
      prevPageNumber.current = PageNumber;
    }
  }, [PageNumber, hasSearched]);

  // No auto-reset of filter — if no results found, show empty state instead of silently switching.

  // Clear search function - resets all fields and search state
  const handleClearSearch = () => {
    // Clear form fields
    setFromLocation("");
    setToLocation("");
    setFlightNumber("");
    setSelectedDates([]);
    setDatesInRange([]);
    setDate(formatDateLocal(new Date(Date.now() + 86400000))); // Reset to tomorrow

    // Close cards and clear search inputs
    setIsFromCardOpen(false);
    setIsToCardOpen(false);
    setFromSearchInput("");
    setToSearchInput("");

    // Clear search results
    setFlights([]);
    setConnectionFlights([]);
    setTotalFlights(0);
    setApiResultCount(0);
    setDirectTotal(undefined);
    setConnectionTotal(undefined);
    setShowResults(false);

    // Reset search state
    setHasSearched(false);
    setPageNumber(1);
    prevPageNumber.current = 1;
    prevStopsFilter.current = "none";
    prevSortBy.current = null;
    prevSortOrder.current = null;

    // Reset filters
    setStopsFilter("none");
    setSortBy(null);
    setSortOrder(null);
    setSelectedDeparture("no");
    setSelectedArrival("no");

    // Reset AI suggestive filter states
    setSuggestions([]);
    setAppliedSuggestionId(null);
    setAppliedSuggestion(null);
    setFacets(null);
    setAppliedDirectiveFilters(null);
    setPriorManualState(null);

    // Clear errors
    setErrors({});

    // Hide results
    onShowSearchResults?.(false);
  };

  // Optimized last search function - loads from localStorage first, falls back to API
  const handleLastSearchClick = async () => {
    setIsLoadingLastSearch(true);
    setIsLoadingFlights(true); // Show skeleton for search

    try {
      // First check localStorage
      let lastSearchData: LastSearchData | null = loadLastSearchFromStorage();

      // If not found in localStorage, fetch from API as fallback
      if (!lastSearchData) {
        showLoader(); // Show global loader for API call
        try {
          const response = await getLastRoute();
          const lastRouteData: LastRouteResponse = response.data || response;

          if (lastRouteData && lastRouteData.lastSearchRoute) {
            const {
              source,
              destination,
              date: lastDate,
            } = lastRouteData.lastSearchRoute;

            // Convert single date to array format for storage
            lastSearchData = {
              source,
              destination,
              dates: [lastDate],
              savedAt: Date.now(),
            };

            // Save to localStorage for future use
            saveLastSearchToStorage(source, destination, [lastDate]);
          } else {
            hideLoader();
            setIsLoadingLastSearch(false);
            setIsLoadingFlights(false);
            return;
          }
        } catch (error) {
          console.error("Error fetching last search from API:", error);
          hideLoader();
          setIsLoadingLastSearch(false);
          setIsLoadingFlights(false);
          return;
        } finally {
          hideLoader();
        }
      }

      // Populate fields with stored data
      if (lastSearchData) {
        const { source, destination, dates } = lastSearchData;

        // Set locations
        setFromLocation(source);
        setToLocation(destination);

        // Convert date strings to Date objects for calendar
        const dateObjects = dates
          .map((dateStr) => {
            const date = new Date(dateStr);
            // Validate date
            if (isNaN(date.getTime())) {
              return null;
            }
            return date;
          })
          .filter((date): date is Date => date !== null);

        // Set dates
        setSelectedDates(dateObjects);
        setDatesInRange(dates);

        // Set date state for backward compatibility (use first date if available)
        if (dates.length > 0) {
          setDate(dates[0]);
        }

        // Clear any errors
        setErrors({});

        // Set search state
        setHasSearched(true);
        onShowSearchResults?.(true);
        prevPageNumber.current = 1;
        setPageNumber(1);

        // Wait for state updates to complete, then auto-execute search with direct values
        await new Promise((resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              resolve(undefined);
            });
          });
        });

        // Call getFlights with direct values to ensure they're used. The
        // currently selected Filter/Sort (if any) carry over into this search
        // rather than resetting — they're chosen up front in the search form.
        await getFlights({
          source,
          destination,
          dates,
          pageNumber: 1,
        });

        // Scroll to search results
        setTimeout(() => {
          const searchResultsElement =
            document.getElementById("search-results");
          if (searchResultsElement) {
            searchResultsElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      }
    } catch (error) {
      console.error("Error loading last search:", error);
    } finally {
      setIsLoadingLastSearch(false);
      // Note: isLoadingFlights will be set to false by getFlights() finally block
    }
  };

  const getFlights = async (overrideParams?: {
    source?: string;
    destination?: string;
    flightNumber?: string;
    dates?: string[];
    pageNumber?: number;
    stopsFilter?: "none" | "one-or-fewer" | "one" | "two" | "three" | null;
    sortBy?: string | null;
    sortOrder?: DurationSortOrder | string | null;
    directiveFilters?: FlightSuggestionDirectiveFilters | null;
    appliedSuggestionId?: string | null;
    skipSuggestionsFetch?: boolean;
  }) => {
    // Set loading state - will be reset in finally block
    setIsLoadingFlights(true);
    setSearchError(null);
    setNoResultsMessage(null);
    try {
      // Map stops filter to API parameters
      let { Stops, IncludeDirect, IncludeConnections } =
        mapStopsFilterToParams(overrideParams?.stopsFilter ?? stopsFilter);

      // Use override params if provided, otherwise use state values
      const sourceValue = overrideParams?.source ?? fromLocation;
      const destinationValue = overrideParams?.destination ?? toLocation;
      const datesValue = overrideParams?.dates ?? datesInRange;
      const pageNumberValue = overrideParams?.pageNumber ?? PageNumber;
      const sortByValue =
        overrideParams?.sortBy !== undefined ? overrideParams.sortBy : sortBy;
      const sortOrderValue =
        overrideParams?.sortOrder !== undefined
          ? overrideParams.sortOrder
          : sortOrder;
      const activeDirectiveFilters =
        overrideParams?.directiveFilters !== undefined
          ? overrideParams.directiveFilters
          : appliedDirectiveFilters;
      const activeAppliedSuggestionId =
        overrideParams?.appliedSuggestionId !== undefined
          ? overrideParams.appliedSuggestionId
          : appliedSuggestionId;

      if (activeDirectiveFilters) {
        if (activeDirectiveFilters.includeDirect !== undefined) {
          IncludeDirect = activeDirectiveFilters.includeDirect;
        }
        if (activeDirectiveFilters.includeConnections !== undefined) {
          IncludeConnections = activeDirectiveFilters.includeConnections;
        }
        if (activeDirectiveFilters.stops !== undefined) {
          Stops = activeDirectiveFilters.stops;
        }
      }

      const isMonthSearch = datesValue.some((d) => {
        if (!d.includes(" - ")) return false;
        const [startStr, endStr] = d.split(" - ");
        const dStart = new Date(`${startStr}T00:00:00`);
        const dEnd = new Date(`${endStr}T00:00:00`);
        if (isNaN(dStart.getTime()) || isNaN(dEnd.getTime())) return false;
        const diffDays = Math.round(
          (dEnd.getTime() - dStart.getTime()) / (1000 * 3600 * 24)
        );
        return diffDays > 14;
      });

      const flightNumberValue = normalizeFlightNumber(
        overrideParams?.flightNumber !== undefined
          ? overrideParams.flightNumber
          : flightNumber,
      );

      // Route + optional flight number go out as ONE request — the API applies
      // the flight number to direct, nearby-airport and connecting results.
      const params = {
        ...(sourceValue ? { Source: sourceValue } : {}),
        ...(destinationValue ? { Destination: destinationValue } : {}),
        ...(flightNumberValue ? { FlightNumber: flightNumberValue } : {}),
        IsNearbyDeparture: selectedDeparture === "yes",
        IsNearbyArrival: selectedArrival === "yes",
        Dates: datesValue,
        PageNumber: pageNumberValue,
        PageSize: isMonthSearch ? 50 : flightsPerPage,
        ...(sortByValue ? { SortBy: sortByValue } : {}),
        ...(sortOrderValue ? { SortOrder: sortOrderValue } : {}),
        IncludeDirect,
        IncludeConnections,
        ...(Stops !== undefined ? { Stops } : {}),
        ...(activeDirectiveFilters?.leaveTime
          ? { LeaveTime: activeDirectiveFilters.leaveTime }
          : {}),
        ...(activeDirectiveFilters?.arrivalTime
          ? { ArrivalTime: activeDirectiveFilters.arrivalTime }
          : {}),
        ...(activeDirectiveFilters?.airlineName
          ? { AirlineName: activeDirectiveFilters.airlineName }
          : {}),
      };

      // Fetch flights (GET request)
      const response = await fetchFlights(params);
      const rawFlightData = response.data.items || [];
      const connectionsData = (response.data.connections ||
        []) as ConnectionFlight[];

      // If we requested connections but API returned only direct flights,
      // still show results rather than silently reverting the filter.

      const flightData = rawFlightData as FlightDetails[];

      // A valid zero-result response (e.g. a flight number that doesn't fly
      // the selected route) — surface the API's own explanation, if given.
      setNoResultsMessage(
        flightData.length === 0 && connectionsData.length === 0
          ? response.message || null
          : null,
      );

      const flightsWithCompositeIds = withDirectFlightCompositeIds(flightData);
      const connectionsWithCompositeIds =
        withConnectionCompositeIds(connectionsData);

      setFlights(flightsWithCompositeIds);
      setConnectionFlights(connectionsWithCompositeIds);
      setApiResultCount(response.count ?? 0);

      // Total = direct single-flight results (data.total) + multi-leg
      // connections (connectionTotal). A 1/2/3-stop search returns BOTH — e.g. a
      // single-flight-number 1-stop like AI101 in `items`, PLUS multi-airline
      // connections — so the count is the sum. Non-stop has only `total`,
      // Connected only `connectionTotal`; the missing side counts as 0.
      const responseDirectTotal = response.data.total ?? 0;
      const responseConnTotal = response.data.connectionTotal ?? 0;
      setDirectTotal(response.data.total);
      setConnectionTotal(response.data.connectionTotal);

      if (
        response.data.total !== undefined ||
        response.data.connectionTotal !== undefined
      ) {
        const activeFilter = overrideParams?.stopsFilter ?? stopsFilter;
        setTotalFlights(
          calculateEffectiveTotalFlights(
            activeFilter,
            response.data.total,
            response.data.connectionTotal,
          ),
        );
      } else {
        // Fallback: estimate based on current page results. Must use the SAME
        // page size as the request above (50 for month/week searches) or the
        // estimate — and the totalPages derived from it — drifts from what
        // was actually requested.
        const requestPageSize = isMonthSearch ? 50 : flightsPerPage;
        const estimatedTotal =
          flightData.length + connectionsData.length < requestPageSize
            ? (PageNumber - 1) * requestPageSize +
              flightData.length +
              connectionsData.length
            : PageNumber * requestPageSize;
        setTotalFlights(estimatedTotal);
      }

      setShowResults(flightData.length > 0 || connectionsData.length > 0);

      // Fetch flight suggestions in background on initial search / material filter changes (never on pagination alone)
      if (pageNumberValue === 1 && !overrideParams?.skipSuggestionsFetch) {
        const responseFacets =
          (response as any)?.facets ||
          (response as any)?.data?.facets ||
          (response as any)?.Facets ||
          (response as any)?.data?.Facets ||
          null;

        console.log("[FlightSearch Response facets]", responseFacets);
        setFacets(responseFacets);

        // IncludeDirect/IncludeConnections/Stops above are the raw over-fetch
        // params (see mapStopsFilterToParams — "none" deliberately asks for
        // connections too, then the display hides them client-side). The
        // suggestions call needs what's actually shown, not what was fetched.
        const displayIntent = mapStopsFilterToDisplayIntent(
          overrideParams?.stopsFilter ?? stopsFilter,
        );
        let contextIncludeDirect = displayIntent.IncludeDirect;
        let contextIncludeConnections = displayIntent.IncludeConnections;
        let contextStops = displayIntent.Stops;
        if (activeDirectiveFilters) {
          if (activeDirectiveFilters.includeDirect !== undefined) {
            contextIncludeDirect = activeDirectiveFilters.includeDirect;
          }
          if (activeDirectiveFilters.includeConnections !== undefined) {
            contextIncludeConnections = activeDirectiveFilters.includeConnections;
          }
          if (activeDirectiveFilters.stops !== undefined) {
            contextStops = activeDirectiveFilters.stops;
          }
        }

        const searchContext = {
          source: sourceValue ? [sourceValue] : [],
          destination: destinationValue ? [destinationValue] : [],
          dates: Array.isArray(datesValue) ? datesValue : [datesValue],
          includeDirect: contextIncludeDirect,
          includeConnections: contextIncludeConnections,
          ...(contextStops !== undefined ? { stops: contextStops } : {}),
        };
        const locale = (
          typeof navigator !== "undefined" && navigator.language
            ? navigator.language.split("-")[0]
            : "EN"
        ).toUpperCase();

        console.log("[FlightSuggestions Payload]", {
          searchContext,
          facets: responseFacets,
          appliedSuggestionId: activeAppliedSuggestionId,
          locale,
        });

        const suggestionsRequestId = ++suggestionsRequestSeq.current;
        fetchFlightSuggestions({
          searchContext,
          facets: responseFacets,
          appliedSuggestionId: activeAppliedSuggestionId,
          locale,
        })
          .then((res) => {
            if (suggestionsRequestId !== suggestionsRequestSeq.current) return;
            if (res && Array.isArray(res.suggestions)) {
              setSuggestions(res.suggestions);
            } else {
              setSuggestions([]);
            }
          })
          .catch(() => {
            if (suggestionsRequestId !== suggestionsRequestSeq.current) return;
            setSuggestions([]);
          });
      }
    } catch (err: any) {
      const apiMessage = err.response?.data?.message;
      const status = err.response?.status;
      // "No flights found" is a valid empty result, not an error — the inline
      // empty state already communicates it, so don't also raise a red toast.
      const isNoResults =
        status === 404 ||
        (typeof apiMessage === "string" &&
          /no flights?\s+found|no\s+results?/i.test(apiMessage));
      if (!isNoResults) {
        const message = apiMessage || "Failed to fetch flights.";
        useToastStore.getState().showToast(message, "error");
        setSearchError(message);
      }
      setFlights([]);
      setConnectionFlights([]);
      setTotalFlights(0);
      setApiResultCount(0);
      setDirectTotal(undefined);
      setConnectionTotal(undefined);
      setShowResults(false);
    } finally {
      setIsLoadingFlights(false);
    }
  };

  // Fetches every page of the current search so "Select all" can select
  // results beyond the page currently on screen. Reuses the current
  // filters/sort/search params, only varying PageNumber.
  const fetchAllFlightsAndConnections = useCallback(async (): Promise<{
    flights: FlightDetails[];
    connections: ConnectionFlight[];
  }> => {
    if (totalPages <= 1) {
      return { flights, connections: connectionFlights };
    }

    const { Stops, IncludeDirect, IncludeConnections } =
      mapStopsFilterToParams(stopsFilter);

    const normalizedFlightNumber = normalizeFlightNumber(flightNumber);

    const baseParams = {
      ...(fromLocation ? { Source: fromLocation } : {}),
      ...(toLocation ? { Destination: toLocation } : {}),
      ...(normalizedFlightNumber
        ? { FlightNumber: normalizedFlightNumber }
        : {}),
      IsNearbyDeparture: selectedDeparture === "yes",
      IsNearbyArrival: selectedArrival === "yes",
      Dates: datesInRange,
      PageSize: effectivePageSize,
      ...(sortBy ? { SortBy: sortBy } : {}),
      ...(sortOrder ? { SortOrder: sortOrder } : {}),
      IncludeDirect,
      IncludeConnections,
      ...(Stops !== undefined ? { Stops } : {}),
    };

    const responses = await Promise.all(
      Array.from({ length: totalPages }, (_, index) =>
        fetchFlights({ ...baseParams, PageNumber: index + 1 }),
      ),
    );

    const rawFlights = responses.flatMap(
      (r) => (r.data.items || []) as FlightDetails[],
    );
    const rawConnections = responses.flatMap(
      (r) => (r.data.connections || []) as ConnectionFlight[],
    );

    return {
      flights: withDirectFlightCompositeIds(rawFlights),
      connections: withConnectionCompositeIds(rawConnections),
    };
  }, [
    totalPages,
    flights,
    connectionFlights,
    stopsFilter,
    sortBy,
    sortOrder,
    fromLocation,
    toLocation,
    flightNumber,
    selectedDeparture,
    selectedArrival,
    datesInRange,
    effectivePageSize,
  ]);

  const getExistingVoiceCriteria = useCallback(
    (): Partial<VoiceCriteria> => ({
      source: fromLocation ? [fromLocation] : null,
      destination: toLocation ? [toLocation] : null,
      dates: datesInRange.length > 0 ? datesInRange : null,
      isNearbyDeparture: selectedDeparture === "yes",
      isNearbyArrival: selectedArrival === "yes",
    }),
    [fromLocation, toLocation, datesInRange, selectedDeparture, selectedArrival],
  );

  const voice = useWebVoiceSearch(getExistingVoiceCriteria);
  const pendingAutoSearch = useRef(false);

  // Apply the mapped result to the form as soon as review is ready. Only
  // ever fills fields that came back non-null — never blanks something the
  // user already typed — and flags (never hides) low-confidence fields.
  useEffect(() => {
    if (voice.state.name !== "review") return;
    const { result } = voice.state;

    if (result.targetForm === "flightNumber") {
      const heardFlightNumber = normalizeFlightNumber(
        result.flightNumberFields.flightNumber,
      );
      if (heardFlightNumber) {
        // This form owns the flight number field now, so a heard flight number
        // always lands here — with a route it narrows the route search, without
        // one it searches on the flight number alone.
        setFlightNumber(heardFlightNumber);
      } else {
        // airlineName-only — no flight number to put in the field.
        useToastStore
          .getState()
          .showToast("Heard an airline but no flight number — say the full flight number.", "error", 4000);
        voice.discardReview();
        return;
      }
    }

    // "Heard: cochin → COK" — makes a wrong match obvious at a glance
    // instead of a mystery airport code silently appearing in the field.
    const heard: string[] = [];
    if (result.routeFields.fromLocation && result.spans.sourceText) {
      heard.push(`"${result.spans.sourceText}" → ${result.routeFields.fromLocation}`);
    }
    if (result.routeFields.toLocation && result.spans.destinationText) {
      heard.push(`"${result.spans.destinationText}" → ${result.routeFields.toLocation}`);
    }
    if (heard.length > 0) {
      useToastStore.getState().showToast(`Heard: ${heard.join(", ")}`, "success", 4000);
    }

    if (result.routeFields.fromLocation) setFromLocation(result.routeFields.fromLocation);
    if (result.routeFields.toLocation) setToLocation(result.routeFields.toLocation);
    // Auto-search once voice fills a complete route + date — the user
    // already confirmed the transcript, so this is the last step, not
    // another form for them to submit manually.
    const nextFrom = result.routeFields.fromLocation ?? fromLocation;
    const nextTo = result.routeFields.toLocation ?? toLocation;
    const nextHasDates =
      (result.routeFields.dates && result.routeFields.dates.length > 0) ||
      selectedDates.length > 0;
    if (nextFrom && nextTo && nextFrom !== nextTo && nextHasDates) {
      pendingAutoSearch.current = true;
    }
    if (result.routeFields.isNearbyDeparture !== undefined) {
      setSelectedDeparture(result.routeFields.isNearbyDeparture ? "yes" : "no");
    }
    if (result.routeFields.isNearbyArrival !== undefined) {
      setSelectedArrival(result.routeFields.isNearbyArrival ? "yes" : "no");
    }
    if (result.routeFields.dates && result.routeFields.dates.length > 0) {
      setDatesInRange(result.routeFields.dates);
      setSelectedDates(result.routeFields.dates.map((d) => new Date(d)));
    }

    if (result.flaggedFields.length > 0) {
      useToastStore
        .getState()
        .showToast(
          `Please double-check: ${result.flaggedFields.join(", ")} (low confidence).`,
          "error",
          5000,
        );
    }

    // leaveTime/arrivalTime are never applied as a filter — the backend
    // pins their confidence at 0.3 by design because LoadRepository doesn't
    // filter on them yet. Surface as an unconfirmed suggestion only.
    if (result.timeSuggestions.leaveTime || result.timeSuggestions.arrivalTime) {
      const parts = [
        result.timeSuggestions.leaveTime && `leave ~${result.timeSuggestions.leaveTime}`,
        result.timeSuggestions.arrivalTime && `arrive ~${result.timeSuggestions.arrivalTime}`,
      ].filter(Boolean);
      useToastStore
        .getState()
        .showToast(
          `Heard "${parts.join(", ")}" — not applied, time filtering isn't available yet.`,
          "error",
          5000,
        );
    }

    voice.discardReview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice.state]);

  // Fires once the state updates queued above land, so fromLocation/
  // toLocation/selectedDates here are the post-voice values, not stale ones.
  useEffect(() => {
    if (!pendingAutoSearch.current) return;
    pendingAutoSearch.current = false;
    if (fromLocation && toLocation && fromLocation !== toLocation && selectedDates.length > 0) {
      void handleSearchClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromLocation, toLocation, selectedDates]);

  useEffect(() => {
    // needsInput has no toast here — it renders VoiceAmbiguityChooser instead.
    if (voice.state.name === "denied") {
      useToastStore.getState().showToast("Microphone permission denied.", "error", 4000);
      voice.reset();
    }
    if (voice.state.name === "modelFailed") {
      useToastStore.getState().showToast("Voice search isn't available right now.", "error", 4000);
      voice.reset();
    }
    if (voice.state.name === "failed") {
      const messages: Partial<Record<string, string>> = {
        NO_SPEECH_DETECTED: "Didn't catch that — try again.",
        UNINTELLIGIBLE: "Couldn't understand that — try again.",
        NOT_A_FLIGHT_SEARCH:
          'Try something like: "flights from Cochin to Dubai tomorrow".',
        TIMEZONE_UNRESOLVED: "Voice search hit a timezone issue — please search manually.",
        TIMEOUT: "That's taking too long — please try again or search manually.",
        MIC_IN_USE: "Microphone is being used by another app.",
        OFFLINE: "You appear to be offline — voice search needs a connection.",
        AUDIO_TOO_LARGE: "That recording was too long — try a shorter phrase.",
      };
      const message =
        messages[voice.state.reasonCode] ??
        "Voice search failed — please try again or search manually.";
      useToastStore.getState().showToast(message, "error", 4000);
      voice.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice.state]);

  const isVoicePanelActive =
    voice.state.name === "permissionPrompt" ||
    voice.state.name === "modelLoading" ||
    voice.state.name === "listening" ||
    voice.state.name === "transcribing" ||
    voice.state.name === "interpreting";

  const voiceElapsedSeconds =
    voice.state.name === "listening" ? Math.floor(voice.state.elapsedMs / 1000) : 0;

  const voiceModelLoadProgress =
    voice.state.name === "modelLoading" ? voice.state.progress : 0;

  const voiceLevel = voice.state.name === "listening" ? voice.state.level : 0;

  const handleSearchClick = async () => {
    // Simple validation check when the search button is clicked
    const newErrors: typeof errors = {};
    // Either a full From+To route, or a flight number, is enough to search.
    // From/To are a pair though — entering only one of them is never valid,
    // flight number or not. Date is always required either way.
    const hasFlightNumber = !!flightNumber.trim();
    const hasFrom = !!fromLocation;
    const hasTo = !!toLocation;
    const hasFullRoute = hasFrom && hasTo;
    const hasPartialRoute = (hasFrom || hasTo) && !hasFullRoute;
    if (hasPartialRoute) {
      if (!hasFrom) newErrors.from = "*Please select departure location.";
      if (!hasTo) newErrors.to = "*Please select arrival location.";
    } else if (!hasFullRoute && !hasFlightNumber) {
      newErrors.from = "*Please select departure location.";
      newErrors.to = "*Please select arrival location.";
    }
    if (selectedDates.length === 0)
      newErrors.date = "*Please select at least one date.";
    if (hasFlightNumber && !isValidFlightNumber(flightNumber)) {
      newErrors.flightNumber =
        "Please select a valid flight number.";
    }
    if (fromLocation && toLocation && fromLocation === toLocation) {
      newErrors.from = "Departure and arrival locations cannot be the same.";
      newErrors.to = "Departure and arrival locations cannot be the same.";
    }
    setErrors(newErrors);

    // Only show the search results area when validation passes
    if (Object.keys(newErrors).length > 0) return;

    setHasSearched(true);
    onShowSearchResults?.(true);
    prevPageNumber.current = 1; // Reset prev page number for new search
    setPageNumber(1); // Reset to first page on new search

    // The Filter/Sort chosen up front in the search form carry into this
    // search rather than resetting. Sync the refs to the current values so
    // the filter/sort-change effect doesn't fire a redundant refetch right
    // after this one.
    prevStopsFilter.current = stopsFilter;
    prevSortBy.current = sortBy;
    prevSortOrder.current = sortOrder;

    try {
      // Set loading state first to show skeleton
      setIsLoadingFlights(true);
      // Small delay to ensure skeleton renders
      await new Promise((resolve) => setTimeout(resolve, 50));
      // Save the recent search in parallel with the flight search.
      const recentPayload: SaveRecentSearchPayload = {};
      if (fromLocation && toLocation) {
        recentPayload.sourceCode = fromLocation;
        recentPayload.destinationCode = toLocation;
      }
      if (flightNumber && flightNumber.trim() !== "") {
        recentPayload.flightNumber = flightNumber.trim();
      }

      await Promise.all([
        getFlights({ stopsFilter }),
        (recentPayload.sourceCode || recentPayload.flightNumber
          ? saveRecentSearch(recentPayload)
          : Promise.resolve()
        ).then(fetchRecentRouteSearches),
      ]);

      // Save search data to localStorage after successful search
      if (fromLocation && toLocation && datesInRange.length > 0) {
        saveLastSearchToStorage(fromLocation, toLocation, datesInRange);
      }

      // Scroll to search results after a brief delay to ensure content is rendered
      setTimeout(() => {
        const searchResultsElement = document.getElementById("search-results");
        if (searchResultsElement) {
          searchResultsElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    } catch (error) {
      console.error("Error fetching flights:", error);
    }
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const dateStr = formatDateLocal(date);
      const dateIndex = selectedDates.findIndex(
        (d) => formatDateLocal(d) === dateStr,
      );

      if (dateIndex > -1) {
        // Date already selected, remove it
        const newDates = selectedDates.filter(
          (_, index) => index !== dateIndex,
        );
        setSelectedDates(newDates);
      } else {
        // Add new date
        const newDates = [...selectedDates, date].sort(
          (a, b) => a.getTime() - b.getTime(),
        );
        setSelectedDates(newDates);
      }

      // Clear date error when a date is selected
      if (errors.date) {
        setErrors((prev) => ({ ...prev, date: undefined }));
      }
    }
  };

  const handleSaveDates = async (
    overrideDatesInRange?: string[],
    overrideSelectedDates?: Date[]
  ) => {
    const datesToSave =
      overrideDatesInRange ?? selectedDates.map((d) => formatDateLocal(d));
    const selDates = overrideSelectedDates ?? selectedDates;

    setDatesInRange(datesToSave);
    setSelectedDates(selDates);
    setIsCalendarOpen(false);

    // Auto-trigger search if all fields are filled
    if (fromLocation && toLocation && datesToSave.length > 0) {
      setTimeout(async () => {
        try {
          // Set search state
          setHasSearched(true);
          onShowSearchResults?.(true);
          prevPageNumber.current = 1;
          setPageNumber(1);

          // The Filter/Sort chosen up front in the search form carry into
          // this search rather than resetting. Sync the refs so the
          // filter/sort-change effect doesn't fire a redundant refetch.
          prevStopsFilter.current = stopsFilter;
          prevSortBy.current = sortBy;
          prevSortOrder.current = sortOrder;

          // Clear any previous errors
          setErrors({});

          // Set loading state first to show skeleton
          setIsLoadingFlights(true);
          // Small delay to ensure skeleton renders
          await new Promise((resolve) => setTimeout(resolve, 50));

          // Call getFlights with override params to ensure dates are passed,
          // and save the recent search in parallel (mirrors handleSearchClick).
          const recentPayload: SaveRecentSearchPayload = {};
          if (fromLocation && toLocation) {
            recentPayload.sourceCode = fromLocation;
            recentPayload.destinationCode = toLocation;
          }
          if (flightNumber && flightNumber.trim() !== "") {
            recentPayload.flightNumber = flightNumber.trim();
          }

          await Promise.all([
            getFlights({
              source: fromLocation,
              destination: toLocation,
              dates: datesToSave,
              pageNumber: 1,
            }),
            (recentPayload.sourceCode || recentPayload.flightNumber
              ? saveRecentSearch(recentPayload)
              : Promise.resolve()
            ).then(fetchRecentRouteSearches),
          ]);

          // Save search data to localStorage after successful search
          saveLastSearchToStorage(fromLocation, toLocation, datesToSave);

          // Scroll to search results after a brief delay to ensure content is rendered
          setTimeout(() => {
            const searchResultsElement =
              document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights:", error);
        }
      }, 100);
    }
  };

  const clearAllDates = () => {
    setSelectedDates([]);
    setDatesInRange([]);
  };

  const searchResults = useMemo(() => {
    if (!hasSearched) return null;
    const resultCount =
      Number(totalFlights) || flights.length + connectionFlights.length;
    return (
      <div id="search-results" className="pt-2 sm:pt-4">
        <SearchResults
          flights={flights}
          connectionFlights={connectionFlights}
          stopsFilter={stopsFilter}
          onStopsFilterChange={handleStopsFilterChange}
          sortBy={sortBy}
          sortOrder={sortOrder as DurationSortOrder | null}
          onSortChange={handleSortChange}
          isLoadingFlights={isLoadingFlights}
          searchError={searchError}
          noResultsMessage={noResultsMessage}
          show={true}
          searchDate={date}
          selectedDates={datesInRange}
          onPostRequestSuccess={(items, options) => {
            // A post from a single flight card keeps the results and the search
            // criteria on screen; only the bottom-bar post returns home.
            if (options?.keepResults) {
              onPostRequestSuccess?.(items, options);
              return;
            }
            handleClearSearch();
            onPostRequestSuccess?.(items, options);
          }}
          sourceLabel={fromLocation}
          destinationLabel={toLocation}
          totalResults={totalFlights}
          apiResultCount={apiResultCount}
          directTotal={directTotal}
          connectionTotal={connectionTotal}
          onChangeDate={() => setIsCalendarOpen(true)}
          onChangeRoute={() => setIsFromCardOpen(true)}
          onWidenSearch={() => setIsStopsOpen(true)}
          onFetchAllForSelection={fetchAllFlightsAndConnections}
          appliedSuggestionId={appliedSuggestionId}
          appliedSuggestion={appliedSuggestion}
          onDismissSuggestion={handleDismissSuggestion}
          suggestions={suggestions}
          hideInternalFilters={true}
          showSortInHeader={true}
          hideConnectedFilter={readOnly}
          readOnly={readOnly}
          page={PageNumber}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
    );
  }, [
    hasSearched,
    flights,
    connectionFlights,
    stopsFilter,
    sortBy,
    sortOrder,
    handleSortChange,
    appliedSuggestionId,
    appliedSuggestion,
    handleDismissSuggestion,
    suggestions,
    PageNumber,
    totalPages,
    totalFlights,
    apiResultCount,
    directTotal,
    connectionTotal,
    fetchAllFlightsAndConnections,
    date,
    datesInRange,
    onPostRequestSuccess,
  ]);

  return (
    <div className="mt-0">
      <style>{datePickerStyles}</style>

      {/* Tabs + Form — matching Screenshot 2 & 3 */}
      <div className="sticky top-0 z-20 relative pt-0 -mt-[10px] pb-1">
        {/* Soft orange glow peeking out from behind the search card + AI Assist
            strip — the "3D orange" look from Figma sits behind the white
            cards, not as their own background. Only shown once the AI Assist
            strip itself is actually showing (a suggestion came back),
            matching its own render condition below. */}
        {hasSearched &&
          (flights.length > 0 || connectionFlights.length > 0) &&
          suggestions.length > 0 && (
            <div
              className="pointer-events-none absolute -inset-x-6 -top-6 bottom-0 -z-10 rounded-[32px] blur-xl opacity-60"
              style={{
                backgroundImage:
                  "radial-gradient(160% 160% at 50% 100%, #FFB347 0%, #FFD98E 50%, transparent 80%)",
              }}
              aria-hidden="true"
            />
          )}
        {isVoicePanelActive ? (
          <VoiceListeningPanel
            phase={voice.state.name as VoicePanelPhase}
            elapsedSeconds={voiceElapsedSeconds}
            modelLoadProgress={voiceModelLoadProgress}
            level={voiceLevel}
            onStop={() => voice.stop()}
            onCancel={() => voice.cancel()}
          />
        ) : voice.state.name === "confirm" ? (
          <VoiceTranscriptConfirm
            transcript={voice.state.transcript}
            onConfirm={(text) => voice.confirmTranscript(text)}
            onCancel={() => voice.discardTranscript()}
          />
        ) : voice.state.name === "needsInput" ? (
          <VoiceAmbiguityChooser
            candidates={voice.state.candidates}
            transcript={voice.state.transcript}
            onResolve={(selections) => voice.resolveAmbiguity(selections)}
            onCancel={() => voice.reset()}
          />
        ) : (
          <div
            className={`bg-white p-2.5 sm:p-5 -mx-[10px] overflow-visible rounded-xl border border-[#ECECED] relative z-10 transition-shadow duration-200 ${
              hasSearched
                ? // Results own the page once a search ran — keep the card flat
                  // so it reads as a header strip, not a floating panel.
                  "shadow-[0_-2px_7px_rgba(0,0,0,0.04),0_1px_3px_rgba(0,0,0,0.06)]"
                : "shadow-[0_-4px_14px_rgba(0,0,0,0.08),0_8px_24px_rgba(0,0,0,0.12)]"
            }`}
          >
            <div>
              {/* Main row: From+To, Flight No. (Optional), Date, Find Flight button, AI Spark button — 1 horizontal row on desktop (md:), responsive stacked on mobile */}
              <div className="flex flex-col md:flex-row items-stretch md:items-center w-full gap-2.5 sm:gap-3 mb-3.5 sm:mb-4 overflow-visible">
                {/* From + Swap + To container */}
                <div className="flex flex-row items-center w-full md:w-auto md:flex-[2.2] min-w-0 h-[52px] sm:h-[56px] border border-[#ECECED] rounded-lg overflow-visible relative bg-white">
                  {/* From Field */}
                  <AirportField
                    type="from"
                    label="From"
                    selectedCode={fromLocation}
                    displayValue={resolveFieldDisplay(
                      fromLocation,
                      departureOptions,
                      recentFromLocations,
                    )}
                    options={departureOptions}
                    recentLocations={recentFromLocations}
                    isOpen={isFromCardOpen}
                    setOpen={setIsFromCardOpen}
                    searchInput={fromSearchInput}
                    setSearchInput={setFromSearchInput}
                    isLoading={isLoadingDepartures}
                    onSearch={loadDepartureCodes}
                    onSelect={handleSelectFrom}
                    onClear={() => {
                      setFromLocation("");
                      setIsFromCardOpen(false);
                      setFromSearchInput("");
                    }}
                    onClose={() => {
                      setIsFromCardOpen(false);
                      setFromSearchInput("");
                    }}
                    error={errors.from}
                  />

                  {/* Swap icon button */}
                  <div className="flex items-center justify-center shrink-0 px-0.5">
                    <button
                      type="button"
                      onClick={handleSwapLocations}
                      aria-label="Swap departure and arrival"
                      className="w-[38px] h-[26px] sm:w-[42px] sm:h-[28px] rounded-full border border-[#D1D5DB] bg-white flex items-center justify-center text-[#192227] hover:bg-gray-50 transition-all cursor-pointer z-10 shrink-0 shadow-xs"
                    >
                      <svg
                        className="w-4 h-4 text-[#192227]"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M4 8h15" />
                        <path d="M15 4.5l4 3.5-4 3.5" />
                        <path d="M20 16H5" />
                        <path d="M9 12.5l-4 3.5 4 3.5" />
                      </svg>
                    </button>
                  </div>

                  {/* To Field */}
                  <AirportField
                    type="to"
                    label="To"
                    selectedCode={toLocation}
                    displayValue={resolveFieldDisplay(
                      toLocation,
                      arrivalOptions,
                      recentToLocations,
                    )}
                    options={arrivalOptions}
                    recentLocations={recentToLocations}
                    isOpen={isToCardOpen}
                    setOpen={setIsToCardOpen}
                    searchInput={toSearchInput}
                    setSearchInput={setToSearchInput}
                    isLoading={isLoadingArrivals}
                    onSearch={loadArrivalCodes}
                    onSelect={handleSelectTo}
                    onClear={() => {
                      setToLocation("");
                      setIsToCardOpen(false);
                      setToSearchInput("");
                    }}
                    onClose={() => {
                      setIsToCardOpen(false);
                      setToSearchInput("");
                      resetArrivalSearch();
                    }}
                    error={errors.to}
                  />
                </div>

                {/* Flight No. (Optional) field with autocomplete dropdown */}
                <div className="w-full md:w-auto md:flex-[1.1] min-w-0">
                <div
                  ref={flightFieldRef}
                  className="w-full h-[52px] sm:h-[56px] border border-[#ECECED] bg-white rounded-2xl flex flex-col justify-center px-3 sm:px-3.5 relative cursor-pointer"
                >
                  <div
                    className={
                      flightNumber && flightNumber.trim() !== ""
                        ? "flex flex-col justify-center min-w-0 w-full py-1 h-full"
                        : "flex flex-col justify-center min-w-0 w-full"
                    }
                  >
                    {flightNumber && flightNumber.trim() !== "" ? (
                      /* Selected State: Stacked sub-header with small flight icon + label above entered flight number */
                      <div className="flex items-center gap-1 text-[#6B7280] mb-0.5">
                        <img className="w-3 shrink-0" src={VerticalFlightIcon} alt="airline logo" />
                        <span className="text-[11px] sm:text-xs font-semibold font-inter text-[#6B7280] leading-none">
                          Flight No. (Optional)
                        </span>
                      </div>
                    ) : null}
                    {/* The input stays mounted across the empty/filled states above —
                        swapping to two separate <input> elements on the first
                        keystroke was causing React to remount it and drop focus. */}
                    <div
                      className={`relative flex-1 flex items-center min-w-0 w-full ${
                        flightNumber && flightNumber.trim() !== "" ? "" : "gap-2 h-full"
                      }`}
                    >
                      {!(flightNumber && flightNumber.trim() !== "") && (
                        <img className="w-4" src={VerticalFlightIcon} alt="airline logo" />
                      )}
                      <input
                        type="text"
                        value={flightNumber}
                        onChange={(e) => {
                          setFlightNumber(e.target.value);
                          setShowFlightSuggestions(true);
                          if (errors.flightNumber) {
                            setErrors((prev) => ({
                              ...prev,
                              flightNumber: undefined,
                            }));
                          }
                        }}
                        onFocus={(e) => {
                          setShowFlightSuggestions(true);
                          e.target.select();
                        }}
                        onClick={() => setShowFlightSuggestions(true)}
                        placeholder="Flight No. (Optional)"
                        className={
                          flightNumber && flightNumber.trim() !== ""
                            ? "w-full p-0 pr-6 border-none bg-transparent font-inter text-sm sm:text-base text-[#111827] font-semibold leading-tight focus:outline-none focus:ring-0 truncate"
                            : "w-full p-0 pr-1 border-none bg-transparent font-inter text-sm sm:text-[15px] text-[#111827] font-medium sm:font-semibold placeholder-[#6B7280] focus:outline-none focus:ring-0 truncate"
                        }
                        autoComplete="off"
                      />
                      {flightNumber && flightNumber.trim() !== "" && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFlightNumber("");
                            setShowFlightSuggestions(true);
                          }}
                          className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 shrink-0 transition-colors"
                        >
                          {/* <i className="st-close text-xs"></i> */}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Suggestions Popover Dropdown */}
                  {showFlightSuggestions && (
                    <div className="absolute top-full left-0 mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl z-50 max-h-[320px] w-full sm:w-[300px] overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-150">
                      <div className="bg-[#F3F4F6] px-4 py-2.5 border-b border-gray-200/60 flex items-center justify-between shrink-0">
                        <p className="text-xs font-semibold text-[#374151]">
                          Select Flight Number
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowFlightSuggestions(false)}
                          className="text-gray-400 hover:text-black transition-colors p-0.5 rounded-lg"
                          aria-label="Close"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      <div className="overflow-y-auto flex-1 p-2">
                        {isLoadingFlightNumbers ? (
                          <div className="flex justify-center items-center py-4">
                            <div className="w-5 h-5 border-2 border-[#FFBC00] border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        ) : flightNumberOptions.length === 0 ? (
                          <div className="px-4 py-3 text-xs text-gray-400 text-center">
                            No flight numbers found
                          </div>
                        ) : (
                          <div className="space-y-0.5">
                            {flightNumberOptions.map((opt) => {
                              const isSelected = opt.value === flightNumber;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => {
                                    setFlightNumber(opt.value);
                                    setShowFlightSuggestions(false);
                                    setErrors((prev) => ({
                                      ...prev,
                                      flightNumber: undefined,
                                    }));
                                  }}
                                  className={`w-full text-left px-3 py-2 rounded-xl text-xs sm:text-sm font-medium transition-colors flex items-center justify-between cursor-pointer ${
                                    isSelected
                                      ? "bg-[#FFFBEB] text-[#92400E] font-bold"
                                      : "text-[#192227] hover:bg-gray-100"
                                  }`}
                                >
                                  <span>{opt.value}</span>
                                  {isSelected && (
                                    <Check className="w-3.5 h-3.5 text-[#92400E]" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                  {/* Validation error text below the field/dropdown, outside its border */}
                  {errors.flightNumber && (
                    <p
                      title={errors.flightNumber}
                      className="mt-1 text-[10px] sm:text-[11px] text-red-500 font-medium leading-tight truncate"
                    >
                      {errors.flightNumber}
                    </p>
                  )}
                </div>

                {/* Date field */}
                <div className="w-full md:w-auto md:flex-[0.9] min-w-0">
                  <DateRangeCalendar
                    selectedDates={selectedDates}
                    datesInRange={datesInRange}
                    isCalendarOpen={isCalendarOpen}
                    setIsCalendarOpen={setIsCalendarOpen}
                    onDateChange={handleDateChange}
                    onSaveDates={handleSaveDates}
                    onClearDates={clearAllDates}
                    error={
                      selectedDates.length > 0 || datesInRange.length > 0
                        ? undefined
                        : errors.date
                    }
                    popperPlacement="bottom-end"
                    portalId="datepicker-portal"
                    className="h-[52px] sm:h-[56px] border border-[#ECECED] bg-white rounded-lg"
                    showModeSwitcher={!readOnly}
                    minDate={readOnly ? new Date(Date.now() - 86400000) : undefined}
                    maxDate={readOnly ? new Date(Date.now() + 86400000) : undefined}
                  />
                </div>

                {/* Find Your Flight + AI Spark Button */}
                <div className="flex items-center gap-2 shrink-0 w-full md:w-auto">
                  <button
                    type="button"
                    className={`flex-1 md:flex-none px-4 sm:px-6 rounded-2xl shadow-[0_3px_6px_rgba(0,0,0,0.18)] text-[#192227] text-center bg-[#FFBC00] hover:bg-[#F0AF00] text-sm sm:text-base font-bold font-inter flex items-center justify-center gap-2 transition-colors  cursor-pointer whitespace-nowrap h-[52px] sm:h-[56px] ${
                      isLoadingFlights ? "opacity-75 cursor-not-allowed" : ""
                    }`}
                    onClick={handleSearchClick}
                    disabled={isLoadingFlights}
                  >
                    {isLoadingFlights ? (
                      <>
                        <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                        <span>Searching...</span>
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-[#192227] stroke-[2.5]" />
                        Find Your Flight
                      </>
                    )}
                  </button>

                  {/* AI Spark / Voice Button */}
                  <button
                    type="button"
                    aria-label="Voice or AI Search"
                    // Starts the 31 MB model load a few hundred ms before the
                    // tap completes, so the common case never sees "Preparing
                    // voice search...".
                    onPointerDown={() => voice.warmUp()}
                    onClick={() => voice.state.name === "idle" && voice.start()}
                    disabled={voice.state.name !== "idle"}
                    className="w-[52px] h-[52px] sm:w-[56px] sm:h-[56px] bg-black hover:bg-gray-900 text-white rounded-2xl flex items-center justify-center shrink-0 transition-colors shadow-[0_3px_6px_rgba(0,0,0,0.18)] relative group cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <div className="relative flex items-center justify-center">
                      <img src={MicIcon} alt="" aria-hidden="true" className="w-6 h-7 object-contain" />
                    </div>
                  </button>
                </div>
              </div>

              {/* Bottom Row: Nearby Airports Dropdown + Stops Dropdown + Recent Routes + Clear Search */}
              <div className="flex flex-wrap items-center gap-2.5 sm:gap-3.5 mt-3.5 sm:mt-4.5">
                  {/* 1. Nearby Airports Dropdown */}
                  <div className="relative" ref={nearbyAirportsRef}>
                    <button
                      type="button"
                      onClick={() => {
                        setIsNearbyAirportsOpen((prev) => !prev);
                        setIsStopsOpen(false);
                      }}
                      className={`flex items-center gap-2.5 px-4 h-[42px] sm:h-[44px] border rounded-lg text-sm sm:text-[15px] font-medium font-inter transition-colors cursor-pointer ${
                        selectedDeparture === "yes" || selectedArrival === "yes"
                          ? "border-[#FFBC00] bg-[#FFFBEB] text-[#92400E]"
                          : "border-[#ECECED] bg-[#F9FAFB] text-[#192227] hover:bg-gray-100"
                      }`}
                    >
                      {/* <svg
                        className="w-[20px] h-[20px] text-[#4B5563] shrink-0"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 2.5v2.5" />
                        <path d="M10 5h4l1 2.5H9L10 5z" />
                        <path d="M6.5 7.5h11l-1.8 4.5h-7.4L6.5 7.5z" />
                        <path d="M9.5 12v9" />
                        <path d="M14.5 12v4.5h4.5a2 2 0 0 1 2 2v2.5" />
                        <path d="M5.5 21h17" />
                      </svg> */}
                      <img className="w-5" src={AirportIcon} alt="airline logo" 
                      />
                      <span>Nearby Airports</span>
                      <ChevronDown className={`w-4 h-4 text-[#6B7280] transition-transform ${isNearbyAirportsOpen ? "rotate-180" : ""}`} />
                    </button>

                    {isNearbyAirportsOpen && (
                      <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 w-[190px] max-w-[calc(100vw-2rem)] origin-top bg-white rounded-2xl shadow-xl border border-gray-100 p-3 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                        <div className="flex flex-col gap-1">
                          <label
                            className="flex items-center gap-3 py-2 px-1 cursor-pointer select-none group rounded-lg hover:bg-gray-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedDeparture(selectedDeparture === "yes" ? "no" : "yes");
                            }}
                          >
                            <div
                              className={`w-[18px] h-[18px] rounded-[5px] border-[1.8px] flex items-center justify-center transition-colors bg-white ${
                                selectedDeparture === "yes"
                                  ? "border-black"
                                  : "border-[#9CA3AF] group-hover:border-gray-600"
                              }`}
                            >
                              {selectedDeparture === "yes" && (
                                <Check className="w-3.5 h-3.5 text-black stroke-[3]" />
                              )}
                            </div>
                            <span className="text-sm font-semibold text-[#192227]">Departure Airport</span>
                          </label>

                          <label
                            className="flex items-center gap-3 py-2 px-1 cursor-pointer select-none group rounded-lg hover:bg-gray-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedArrival(selectedArrival === "yes" ? "no" : "yes");
                            }}
                          >
                            <div
                              className={`w-[18px] h-[18px] rounded-[5px] border-[1.8px] flex items-center justify-center transition-colors bg-white ${
                                selectedArrival === "yes"
                                  ? "border-black"
                                  : "border-[#9CA3AF] group-hover:border-gray-600"
                              }`}
                            >
                              {selectedArrival === "yes" && (
                                <Check className="w-3.5 h-3.5 text-black stroke-[3]" />
                              )}
                            </div>
                            <span className="text-sm font-semibold text-[#192227]">Arrival Airport</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 2. Stops Dropdown */}
                  <div className="relative" ref={stopsRef}>
                    <button
                      type="button"
                      onClick={() => {
                        setIsStopsOpen((prev) => !prev);
                        setIsNearbyAirportsOpen(false);
                      }}
                      className={`flex items-center gap-2.5 px-4 h-[42px] sm:h-[44px] border rounded-lg text-sm sm:text-[15px] font-medium font-inter transition-colors cursor-pointer ${
                        stopsFilter && stopsFilter !== "none"
                          ? "border-[#FFBC00] bg-[#FFFBEB] text-[#92400E]"
                          : "border-[#ECECED] bg-[#F9FAFB] text-[#192227] hover:bg-gray-100"
                      }`}
                    >
                      {/* <svg
                        className="w-[20px] h-[20px] text-[#192227] shrink-0"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                      
                        <circle cx="21" cy="3.5" r="1.5" fill="currentColor" />
                        
                        <circle cx="8" cy="18" r="1.4" fill="currentColor" />
                        <circle cx="5.5" cy="20.5" r="1.4" fill="currentColor" />
                        <circle cx="3" cy="22.5" r="1.2" fill="currentColor" />
                     
                        <path
                          d="M19 6.5 C19.6 7.1 19.6 7.9 19 8.5 L15.5 12 L18.5 19 C18.8 19.6 18.2 20.2 17.5 19.8 L13.5 14.5 L9.5 17.5 L10 19 L8.5 19.5 L7.5 17.5 L6 16.5 L5.5 15 L7 15.5 L10 12.5 L4.5 7 C4.1 6.3 4.7 5.7 5.4 6 L12.5 9 L16.5 5.5 C17.3 4.8 18.2 5.6 19 6.5 Z"
                          fill="white"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg> */}
                      <img className="w-5" src={FilterFlight} alt="airline logo" />
                      <span>Stops</span>
                      <ChevronDown className={`w-4 h-4 text-[#6B7280] transition-transform ${isStopsOpen ? "rotate-180" : ""}`} />
                    </button>

                    {isStopsOpen && (
                      <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 w-[160px] max-w-[calc(100vw-2rem)] origin-top bg-white rounded-2xl shadow-xl border border-gray-100 p-3 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                        {hasSearched && (
                          <div className="text-xs text-[#737C8C] font-normal mb-2 px-1">
                            Showing {totalFlights || flights.length + connectionFlights.length || 0} results
                          </div>
                        )}

                        <div className="flex flex-col gap-1">
                          {[
                            { value: "none", label: "Non-stop" },
                            { value: "one", label: "1 Stop" },
                            { value: "two", label: "2 Stops" },
                            { value: "one-or-fewer", label: "Connected" },
                          ].map((opt) => {
                            const isSelected = stopsFilter === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => {
                                  handleStopsFilterChange(opt.value as any);
                                  setIsStopsOpen(false);
                                }}
                                className="flex items-center gap-2.5 py-1.5 px-1 w-full text-left cursor-pointer group rounded-lg hover:bg-gray-50"
                              >
                                <div
                                  className={`w-[18px] h-[18px] rounded-full border-[1.8px] flex items-center justify-center transition-all bg-white ${
                                    isSelected
                                      ? "border-black"
                                      : "border-[#9CA3AF] group-hover:border-gray-600"
                                  }`}
                                >
                                  {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-[#FFBC00]" />}
                                </div>
                                <span
                                  className={`text-sm ${
                                    isSelected ? "font-bold text-[#192227]" : "font-medium text-[#192227]"
                                  }`}
                                >
                                  {opt.label}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 3. Recent Routes Dropdown */}
                  <RecentSearchesDropdown
                    label="Recent Routes"
                    items={recentRouteSearches}
                    isLoading={isLoadingRecentSearches}
                    isOpen={isRecentSearchesOpen}
                    setOpen={setIsRecentSearchesOpen}
                    onOpen={fetchRecentRouteSearches}
                    onSelect={handleRecentSearchSelect}
                    buttonClassName="flex items-center gap-2.5 px-4 h-[42px] sm:h-[44px] bg-[#F9FAFB] border border-[#ECECED] rounded-xl hover:bg-gray-100 transition-colors text-sm sm:text-[15px] font-medium font-inter text-[#192227] cursor-pointer"
                    renderItem={(item) => {
                      const hasRoute = !!(item.sourceCode && item.destinationCode);
                      const hasFlight = !!item.flightNumber;

                      return (
                        <div className="flex items-center justify-between gap-3 w-full py-0.5 font-inter">
                          {hasRoute ? (
                            <div className="flex items-center gap-3 text-sm sm:text-[15px]">
                              <span className="font-bold text-[#374151]">{item.sourceCode}</span>
                              <span className="text-gray-400 text-sm">→</span>
                              <span className="font-bold text-[#374151]">{item.destinationCode}</span>
                            </div>
                          ) : (
                            <span className="font-medium text-[#6B7280] text-sm sm:text-[15px]">
                              {item.flightNumber}
                            </span>
                          )}

                          {hasRoute && hasFlight && (
                            <span className="font-medium text-[#6B7280] text-sm sm:text-[15px] ml-auto whitespace-nowrap">
                              {item.flightNumber}
                            </span>
                          )}
                        </div>
                      );
                    }}
                  />

                {/* 4. Clear Search Button */}
                <button
                  className="search-clear-btn flex items-center gap-2 px-4 h-[42px] sm:h-[44px] bg-[#F9FAFB] border border-[#ECECED] rounded-lg hover:bg-gray-100 text-sm sm:text-[15px] font-medium text-gray-500 hover:text-gray-800 transition-colors ml-auto cursor-pointer"
                  onClick={handleClearSearch}
                  type="button"
                >
                  <i className="st-close text-xs sm:text-sm"></i>
                  <span>Clear Search</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AI Suggestive Filters - Placed directly below the black search container */}
        {hasSearched && (flights.length > 0 || connectionFlights.length > 0) && (
          <div className="-mt-3.5 sm:-mt-4 relative z-0 mb-1 px-3.5 sm:px-4">
            <AiSuggestiveFilters
              suggestions={suggestions}
              appliedSuggestionId={appliedSuggestionId}
              onSelectSuggestion={handleSelectSuggestion}
              onDismissSuggestion={handleDismissSuggestion}
              hasDirect={flights.length > 0}
              hasConnections={
                (typeof connectionTotal === "number" && connectionTotal > 0) ||
                connectionFlights.length > 0
              }
              isLoading={isLoadingFlights}
            />
          </div>
        )}
      </div>

      {/* Search results — rendered below the search area */}
      <div>{searchResults}</div>
    </div>
  );
};

export default SearchByRoute;
